/**
 * Canonical module names used in licences and route/nav authorization.
 * When creating modules in Licence Management, use these exact names.
 */
export const APP_MODULES = {
  ESTIMATION: "Estimation",
  ESTIMATION_STRUCTURE: "Estimation Structure",
  ESTIMATION_PIPING: "Estimation Piping",
  ESTIMATION_PV: "Estimation PV",
  EPCM: "EPCM",
  AIMS: "AIMS",
  MATERIAL: "Material",
  SOFTWARE: "Software",
  TASK: "Task",
  EMS: "EMS",
  MCS: "MCS",
  CMS: "CMS",
  VALIDATION: "Validation",
  COS: "COS",
  PCS: "PCS",
  MIS: "MIS",
  RACI: "RACI",
  FABRICATION: "Fabrication",
  LICENCE_ADMIN: "Licence Admin",
};

/**
 * Estimation sub-modules — licensed independently under Estimation.
 * Keys match equipment tab ids in plant estimation (Structure / Piping / PV).
 */
export const ESTIMATION_SUB_MODULES = {
  structure: APP_MODULES.ESTIMATION_STRUCTURE,
  piping: APP_MODULES.ESTIMATION_PIPING,
  pv: APP_MODULES.ESTIMATION_PV,
};

export const ALL_ESTIMATION_MODULE_NAMES = [
  APP_MODULES.ESTIMATION,
  ...Object.values(ESTIMATION_SUB_MODULES),
];

/**
 * EPCM sub-modules — licensed independently under the EPCM hub (/projects).
 * Keys match plant table column accessorKey values.
 */
export const EPCM_SUB_MODULES = {
  tasks: APP_MODULES.TASK,
  estimation: APP_MODULES.ESTIMATION,
  dcs: APP_MODULES.EMS,
  docRegister: APP_MODULES.EMS,
  mcs: APP_MODULES.MCS,
  pcs: APP_MODULES.CMS,
  validation: APP_MODULES.VALIDATION,
  cms: APP_MODULES.COS,
  pms: APP_MODULES.PCS,
  mis: APP_MODULES.MIS,
};

/**
 * Any module that grants some form of plant/project access — used to gate
 * the Projects (EPCM) list and hub itself. Having just one of these (e.g.
 * EMS only) is enough to reach the list; which module cards/table columns
 * are actually visible once there is handled separately by
 * `filterColumnsByLicence` and the module tile filtering in ProjectHub.
 */
export const ALL_PLANT_MODULE_NAMES = [
  APP_MODULES.EPCM,
  ...Object.values(EPCM_SUB_MODULES),
];

export const ALL_APP_MODULE_NAMES = [
  APP_MODULES.EPCM,
  ...Object.values(EPCM_SUB_MODULES),
  ...Object.values(ESTIMATION_SUB_MODULES),
  APP_MODULES.AIMS,
  APP_MODULES.MATERIAL,
  APP_MODULES.SOFTWARE,
  APP_MODULES.RACI,
  APP_MODULES.FABRICATION,
  APP_MODULES.LICENCE_ADMIN,
];

export const normalizeModuleName = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const isAdminUser = (user) =>
  ["admin", "owner"].includes(String(user?.role ?? "").toLowerCase());

/**
 * Emails granted access to the Licence Admin module regardless of role or
 * assigned licence modules.
 */
const LICENCE_ADMIN_OVERRIDE_EMAILS = ["ahmed.samy@chinookdt.com"];

export const isLicenceAdminOverrideUser = (user) =>
  LICENCE_ADMIN_OVERRIDE_EMAILS.includes(
    String(user?.email ?? "").toLowerCase(),
  );

export const extractModuleNames = (licenceOrModules) => {
  if (!licenceOrModules) return [];

  const modules = Array.isArray(licenceOrModules)
    ? licenceOrModules
    : licenceOrModules.modules;

  if (!Array.isArray(modules)) return [];

  return modules
    .map((module) =>
      typeof module === "string" ? module : module?.name ?? "",
    )
    .filter(Boolean);
};

export const hasModuleAccess = (allowedModules, moduleName) => {
  if (!moduleName) return true;
  if (!Array.isArray(allowedModules) || allowedModules.length === 0) {
    return false;
  }

  const target = normalizeModuleName(moduleName);
  return allowedModules.some(
    (module) => normalizeModuleName(module) === target,
  );
};

export const hasAnyModuleAccess = (allowedModules, moduleNames = []) => {
  if (!moduleNames.length) return true;
  return moduleNames.some((name) => hasModuleAccess(allowedModules, name));
};

export const hasEstimationAccess = (allowedModules) =>
  hasAnyModuleAccess(allowedModules, ALL_ESTIMATION_MODULE_NAMES);

export const filterEstimationTabsByLicence = (tabs, hasModule) =>
  tabs.filter((tab) => {
    const requiredModule = ESTIMATION_SUB_MODULES[tab.key];
    return !requiredModule || hasModule(requiredModule);
  });

/**
 * Returns the licence module name required for a pathname, or null if open to all logged-in users.
 * More specific plant sub-routes are checked before generic /plant and /projects rules.
 */
export const getRequiredModuleForPath = (pathname = "") => {
  const path = pathname.split("?")[0];

  const rules = [
    {
      test: (p) => p.startsWith("/licence-management"),
      module: APP_MODULES.LICENCE_ADMIN,
    },
    { test: (p) => p.startsWith("/admin-panel"), module: null },
    {
      test: (p) => p.startsWith("/estimation"),
      modulesAny: ALL_ESTIMATION_MODULE_NAMES,
    },
    { test: (p) => p.startsWith("/ams"), module: APP_MODULES.AIMS },
    {
      test: (p) => p.startsWith("/material-records") || p.startsWith("/materials-database"),
      module: APP_MODULES.MATERIAL,
    },
    { test: (p) => p.startsWith("/softwares"), module: APP_MODULES.SOFTWARE },
    { test: (p) => p.startsWith("/mytasks"), module: APP_MODULES.TASK },
    {
      test: (p) => /^\/plant\/[^/]+\/estimation(?:\/|$)/.test(p),
      modulesAny: ALL_ESTIMATION_MODULE_NAMES,
    },
    {
      test: (p) => /^\/plant\/[^/]+\/dcs(?:\/|$)/.test(p),
      module: APP_MODULES.EMS,
    },
    {
      test: (p) => p.startsWith("/ems-explorer"),
      module: APP_MODULES.EMS,
    },
    {
      test: (p) => /^\/plant\/[^/]+\/document-register(?:\/|$)/.test(p),
      module: APP_MODULES.EMS,
    },
    {
      test: (p) => /^\/plant\/[^/]+\/mcs(?:\/|$)/.test(p),
      module: APP_MODULES.MCS,
    },
    {
      test: (p) => /^\/plant\/[^/]+\/pcs(?:\/|$)/.test(p),
      module: APP_MODULES.CMS,
    },
    {
      test: (p) => /^\/plant\/[^/]+\/pms(?:\/|$)/.test(p),
      module: APP_MODULES.PCS,
    },
    {
      test: (p) => /^\/plant\/[^/]+\/cms(?:\/|$)/.test(p),
      module: APP_MODULES.COS,
    },
    {
      test: (p) => /^\/plant\/[^/]+\/mis(?:\/|$)/.test(p),
      module: APP_MODULES.MIS,
    },
    {
      test: (p) => /^\/plant\/[^/]+\/validation(?:\/|$)/.test(p),
      module: APP_MODULES.VALIDATION,
    },
    {
      test: (p) => /^\/plant\/[^/]+\/task(?:\/|$)/.test(p),
      module: APP_MODULES.TASK,
    },
    {
      test: (p) => /^\/plant\/[^/]+\/open(?:\/|$)/.test(p),
      modulesAny: ALL_PLANT_MODULE_NAMES,
    },
    {
      test: (p) => /^\/plant\/[^/]+$/.test(p),
      modulesAny: ALL_PLANT_MODULE_NAMES,
    },
    {
      test: (p) => p === "/projects" || /^\/projects\//.test(p),
      modulesAny: ALL_PLANT_MODULE_NAMES,
    },
  ];

  const match = rules.find((rule) => rule.test(path));
  if (!match) return null;
  if (match.modulesAny) {
    return { modulesAny: match.modulesAny };
  }
  return { module: match.module ?? null };
};

export const isPathModuleAllowed = (pathname, allowedModules, user) => {
  if (isAdminUser(user)) return true;
  if (isLicenceAdminOverrideUser(user) && pathname.startsWith("/licence-management")) {
    return true;
  }

  const requirement = getRequiredModuleForPath(pathname);
  if (!requirement) return true;
  if (requirement.modulesAny) {
    return hasAnyModuleAccess(allowedModules, requirement.modulesAny);
  }
  return hasModuleAccess(allowedModules, requirement.module);
};

export const PLANT_MODULE_LICENCE_MAP = {
  task: APP_MODULES.TASK,
  estimation: APP_MODULES.ESTIMATION,
  dcs: APP_MODULES.EMS,
  mcs: APP_MODULES.MCS,
  pcs: APP_MODULES.CMS,
  validation: APP_MODULES.VALIDATION,
  cms: APP_MODULES.COS,
  pms: APP_MODULES.PCS,
  mis: APP_MODULES.MIS,
  docRegister: APP_MODULES.EMS,
};

export const filterColumnsByLicence = (
  columns,
  hasModule,
  allowedModules,
  user,
) => {
  if (isAdminUser(user)) return columns;

  return columns.filter((column) => {
    if (column.accessorKey === "estimation") {
      return hasEstimationAccess(allowedModules);
    }
    const requiredModule = EPCM_SUB_MODULES[column.accessorKey];
    return !requiredModule || hasModule(requiredModule);
  });
};
